import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  applyMask,
  findCountryByCode,
  flagUrl,
  placeholderFor,
  type Country,
} from "@/lib/countries";
import { cn } from "@/lib/cn";

type Props = {
  /** Dígitos do número (sem DDI). */
  value: string;
  onChange: (digits: string) => void;
  /** Código ISO do país selecionado (default BR). */
  countryCode?: string;
  onCountryChange?: (code: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
};

/** Input de telefone com seletor de país (bandeira + DDI). Brasil é
 *  pré-selecionado. Aplica máscara por país; valor emitido é só dígitos. */
export function PhoneInputWithCountry({
  value,
  onChange,
  countryCode,
  onCountryChange,
  placeholder,
  className,
  autoFocus,
}: Props) {
  const country = findCountryByCode(countryCode ?? "BR") ?? DEFAULT_COUNTRY;
  const masked = applyMask(value, country.mask);
  const computedPlaceholder = placeholder ?? placeholderFor(country);

  return (
    <div
      className={cn(
        "flex items-center rounded-md border border-zinc-200 bg-white transition-colors focus-within:border-brand-400 focus-within:ring-1 focus-within:ring-brand-400 dark:border-border dark:bg-card",
        className,
      )}
    >
      <CountrySelect
        selected={country}
        onSelect={(c) => onCountryChange?.(c.code)}
      />
      <input
        type="tel"
        inputMode="tel"
        autoFocus={autoFocus}
        value={masked}
        onChange={(e) => {
          const newDigits = e.target.value.replace(/\D/g, "");
          // Se o user pressionou backspace num literal (parêntese, espaço,
          // hífen), os dígitos não mudam — removemos 1 dígito manualmente
          // pra honrar a intenção de deletar.
          if (
            newDigits === value &&
            e.target.value.length < masked.length &&
            value.length > 0
          ) {
            onChange(value.slice(0, -1));
          } else {
            onChange(newDigits);
          }
        }}
        placeholder={computedPlaceholder}
        className="h-9 min-w-0 flex-1 rounded-r-md bg-transparent pl-2 pr-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
      />
    </div>
  );
}

function CountrySelect({
  selected,
  onSelect,
}: {
  selected: Country;
  onSelect: (c: Country) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => {
      const hay = `${c.name} ${c.code} ${c.dialCode}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`País: ${selected.name}`}
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-l-md border-r border-zinc-200 px-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
        >
          <FlagImage code={selected.code} alt={selected.name} size={20} />
          <span className="tabular-nums text-zinc-600 dark:text-muted-foreground">
            +{selected.dialCode}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[280px] overflow-hidden rounded-xl border border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        <div className="flex items-center gap-1.5 border-b border-zinc-100 px-3 py-2 dark:border-border">
          <Search className="h-3.5 w-3.5 text-zinc-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar país..."
            autoFocus
            className="h-7 flex-1 bg-transparent text-[13px] text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
          />
        </div>
        <ul
          className="max-h-[340px] overflow-y-auto overscroll-contain p-1"
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-[12px] text-zinc-400 dark:text-muted-foreground">
              Nenhum país encontrado
            </li>
          ) : (
            filtered.map((c) => {
              const active = c.code === selected.code;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(c);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors",
                      active
                        ? "bg-brand-50 font-semibold text-brand-900 dark:bg-primary/10 dark:text-primary"
                        : "text-zinc-700 hover:bg-zinc-50 dark:text-foreground/80 dark:hover:bg-muted",
                    )}
                  >
                    <FlagImage code={c.code} alt={c.name} size={22} />
                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                    <span className="shrink-0 tabular-nums text-[11px] text-zinc-500 dark:text-muted-foreground">
                      +{c.dialCode}
                    </span>
                    {active && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-brand-600 dark:text-primary" />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Bandeira via flagcdn (SVG crisp, zero dep). Fallback pro código ISO
 *  quando a imagem falha (modo offline, CDN bloqueado, etc). */
function FlagImage({
  code,
  alt,
  size = 20,
}: {
  code: string;
  alt: string;
  size?: number;
}) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <span
        aria-label={alt}
        className="flex shrink-0 items-center justify-center rounded-sm bg-zinc-100 text-[9px] font-bold text-zinc-500 dark:bg-muted dark:text-muted-foreground"
        style={{ width: size, height: Math.round(size * 0.75) }}
      >
        {code}
      </span>
    );
  }
  return (
    <img
      src={flagUrl(code)}
      alt={alt}
      onError={() => setErrored(true)}
      className="shrink-0 rounded-sm object-cover shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]"
      style={{ width: size, height: Math.round(size * 0.75) }}
    />
  );
}
